// Localização: frontend/src/components/Financeiro/GraficoTendencias.jsx
// Gráfico de Tendências Mensais

import React, { useState } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Card from '../UI/Card';
import CardContent from '../UI/CardContent';
import LoadingIndicator from '../Common/LoadingIndicator';
import Alert from '../UI/Alert';

/**
 * @component GraficoTendencias
 * @description Gráfico de área mostrando tendências financeiras ao longo do tempo
 * @param {Array} data - Dados do fluxo de caixa ou dados mensais
 * @param {number} saldoInicial - Saldo inicial das contas bancárias para cálculo acumulado
 * @param {boolean} isLoading - Estado de carregamento
 * @param {string} error - Mensagem de erro
 * @param {string} periodo - Período selecionado (dia, semana, mes, custom)
 */
const GraficoTendencias = ({ data = [], saldoInicial = 0, isLoading = false, error = null, periodo = 'mes' }) => {
    const [viewType, setViewType] = useState('area'); // 'area' ou 'line'

    // Função para formatar o período baseado no tipo
    const formatarPeriodo = (periodoStr) => {
        if (!periodoStr) return 'Período';
        
        // Se for formato de data (YYYY-MM-DD)
        if (/^\d{4}-\d{2}-\d{2}$/.test(periodoStr)) {
            const date = new Date(periodoStr + 'T00:00:00');
            if (periodo === 'dia') {
                return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
            }
            return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
        }
        
        // Se for formato de semana ISO (IYYY-IW do PostgreSQL) - formato: YYYY-WW (semana pode ter 1-2 dígitos)
        // Exemplo: 2024-01, 2024-52
        if (/^\d{4}-\d{1,2}$/.test(periodoStr) && periodo === 'semana') {
            const parts = periodoStr.split('-');
            const week = parseInt(parts[1]);
            const year = parts[0];
            if (week >= 1 && week <= 53) {
                return `Sem ${week}/${year}`;
            }
        }
        
        // Se for formato de mês (YYYY-MM)
        if (/^\d{4}-\d{2}$/.test(periodoStr) && periodo === 'mes') {
            const [year, month] = periodoStr.split('-');
            const date = new Date(parseInt(year), parseInt(month) - 1, 1);
            return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
        }
        
        // Se for formato de semana ISO com W (YYYY-WW)
        if (periodoStr.includes('-W')) {
            const [year, week] = periodoStr.split('-W');
            return `Sem ${week}/${year}`;
        }
        
        // Fallback: tentar detectar se é semana pelo período selecionado
        if (/^\d{4}-\d{1,2}$/.test(periodoStr)) {
            const parts = periodoStr.split('-');
            if (periodo === 'semana' && parseInt(parts[1]) <= 53) {
                return `Sem ${parts[1]}/${parts[0]}`;
            }
            if (periodo === 'mes' && parseInt(parts[1]) <= 12) {
                const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
                return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
            }
        }
        
        return periodoStr;
    };

    if (isLoading) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <h3 className="text-lg font-semibold mb-4">Tendências Financeiras</h3>
                    <div className="py-6">
                        <LoadingIndicator message="Carregando dados de tendências..." />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <h3 className="text-lg font-semibold mb-4">Tendências Financeiras</h3>
                    <div className="py-4">
                        <Alert type="error" message={error} />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <h3 className="text-lg font-semibold mb-4">Tendências Financeiras</h3>
                    <div className="h-96 flex items-center justify-center py-6">
                        <p className="text-gray-500">Sem dados para exibir.</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Função para formatar data sem problemas de timezone
    const formatarDataSemTimezone = (periodoStr) => {
        if (!periodoStr) return 'Período';
        
        // Se for formato de data (YYYY-MM-DD)
        if (/^\d{4}-\d{2}-\d{2}$/.test(periodoStr)) {
            const partes = periodoStr.split('-');
            if (partes.length === 3) {
                const [ano, mes, dia] = partes;
                if (periodo === 'dia') {
                    // Formatar diretamente como DD/MM, evitando conversão de timezone
                    return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}`;
                }
                // Para outros períodos, formatar como DD/MMM
                return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}`;
            }
        }
        
        // Para outros formatos, usar a função original
        return formatarPeriodo(periodoStr);
    };

    // Usar saldo inicial do backend se disponível, senão calcular
    // O backend agora retorna saldo_inicial calculado corretamente (saldo antes do período)
    let saldoInicialNum = 0;
    
    // Se os dados vieram com saldo_inicial (do backend), usar esse valor
    if (data && data.length > 0 && data[0].saldo_inicial !== undefined) {
        saldoInicialNum = parseFloat(data[0].saldo_inicial) || 0;
    } else if (saldoInicial) {
        // Fallback: calcular baseado no saldo atual menos as transações do período
        // Isso é menos preciso, mas funciona se o backend não retornar saldo_inicial
        const totalReceitasPeriodo = data.reduce((sum, item) => sum + (parseFloat(item.receitas) || 0), 0);
        const totalDespesasPeriodo = data.reduce((sum, item) => sum + (parseFloat(item.despesas) || 0), 0);
        saldoInicialNum = parseFloat(saldoInicial) || 0;
        saldoInicialNum = saldoInicialNum - (totalReceitasPeriodo - totalDespesasPeriodo);
    }
    
    // Formatar dados para o gráfico e calcular saldo acumulado
    // Remover saldo_inicial do primeiro item antes de mapear (se existir)
    const dataParaGrafico = data.map(item => {
        const { saldo_inicial, ...rest } = item;
        return rest;
    });
    
    let saldoAcumulado = saldoInicialNum;
    
    const chartData = dataParaGrafico.map(item => {
        const receitas = parseFloat(item.receitas) || 0;
        const despesas = parseFloat(item.despesas) || 0;
        const saldoPeriodo = receitas - despesas;
        
        // Acumular o saldo
        saldoAcumulado += saldoPeriodo;
        
        return {
            periodo: formatarDataSemTimezone(item.periodo || item.mes),
            periodoOriginal: item.periodo || item.mes || 'Período',
            receitas: receitas,
            despesas: despesas,
            saldo: saldoAcumulado // Saldo acumulado, começando do saldo antes do período
        };
    });

    // Tooltip customizado
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0]?.payload;
            const periodoCompleto = data?.periodoOriginal || label;
            
            return (
                <div className="bg-white p-3 border border-gray-300 shadow-md rounded-md text-sm">
                    <p className="font-semibold text-gray-700 mb-2">{periodoCompleto}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="mb-1">
                            {`${entry.name}: ${new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL'
                            }).format(entry.value)}`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Formatar tick do eixo X baseado no período
    const formatXAxisTick = (tickItem) => {
        return formatarPeriodo(tickItem);
    };

    return (
        <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full flex flex-col">
            <CardContent className="pt-6 pb-6 px-6 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Tendências Financeiras</h3>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setViewType('area')}
                            className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                                viewType === 'area'
                                    ? 'bg-emerald-600 text-white shadow-md'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Área
                        </button>
                        <button
                            onClick={() => setViewType('line')}
                            className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                                viewType === 'line'
                                    ? 'bg-emerald-600 text-white shadow-md'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Linha
                        </button>
                    </div>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                    Evolução financeira ao longo do tempo com destaque para tendências
                </p>
                <div className="outline-none flex-1" style={{ outline: 'none' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {viewType === 'area' ? (
                        <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <defs>
                                <linearGradient id="colorReceitas" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                </linearGradient>
                                <linearGradient id="colorDespesas" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                                </linearGradient>
                                <linearGradient id="colorSaldo" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis 
                                dataKey="periodo" 
                                stroke="#555"
                                style={{ fontSize: '12px' }}
                                tickFormatter={formatXAxisTick}
                                angle={periodo === 'dia' ? -45 : 0}
                                textAnchor={periodo === 'dia' ? 'end' : 'middle'}
                                height={periodo === 'dia' ? 60 : 30}
                            />
                            <YAxis 
                                stroke="#555"
                                tickFormatter={(value) => 
                                    new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                        notation: 'compact'
                                    }).format(value)
                                }
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Area 
                                type="monotone" 
                                dataKey="receitas" 
                                stroke="#10b981" 
                                fillOpacity={1} 
                                fill="url(#colorReceitas)"
                                name="Receitas"
                                strokeWidth={2}
                            />
                            <Area 
                                type="monotone" 
                                dataKey="despesas" 
                                stroke="#ef4444" 
                                fillOpacity={1} 
                                fill="url(#colorDespesas)"
                                name="Despesas"
                                strokeWidth={2}
                            />
                            <Area 
                                type="monotone" 
                                dataKey="saldo" 
                                stroke="#3b82f6" 
                                fillOpacity={1} 
                                fill="url(#colorSaldo)"
                                name="Saldo"
                                strokeWidth={2}
                            />
                        </AreaChart>
                    ) : (
                        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis 
                                dataKey="periodo" 
                                stroke="#555"
                                style={{ fontSize: '12px' }}
                                tickFormatter={formatXAxisTick}
                                angle={periodo === 'dia' ? -45 : 0}
                                textAnchor={periodo === 'dia' ? 'end' : 'middle'}
                                height={periodo === 'dia' ? 60 : 30}
                            />
                            <YAxis 
                                stroke="#555"
                                tickFormatter={(value) => 
                                    new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                        notation: 'compact'
                                    }).format(value)
                                }
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line 
                                type="monotone" 
                                dataKey="receitas" 
                                stroke="#10b981" 
                                strokeWidth={3}
                                name="Receitas"
                                dot={{ r: 5 }}
                                activeDot={{ r: 7 }}
                            />
                            <Line 
                                type="monotone" 
                                dataKey="despesas" 
                                stroke="#ef4444" 
                                strokeWidth={3}
                                name="Despesas"
                                dot={{ r: 5 }}
                                activeDot={{ r: 7 }}
                            />
                            <Line 
                                type="monotone" 
                                dataKey="saldo" 
                                stroke="#3b82f6" 
                                strokeWidth={3}
                                name="Saldo"
                                dot={{ r: 5 }}
                                activeDot={{ r: 7 }}
                            />
                        </LineChart>
                    )}
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

export default GraficoTendencias;

